import { Alert, Button, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { LockOpenIcon, LogInIcon, TimerResetIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { type OAuthAccessDeniedCodeType } from '@/api/error-codes';
import { openRequestSink } from '@/api/interceptors';
import { ModalDialog } from '@/components/Dialog/ModalDialog';

export type SessionExceptionModalReason = 'unknown' | 'invalid' | 'expired' | OAuthAccessDeniedCodeType;

export const SessionExceptionModal = ({
  onAccept,
  reason,
}: {
  onAccept: () => Promise<void>;
  reason: SessionExceptionModalReason;
}) => {
  const isExpired = reason === 'expired';
  const acceptedRef = useRef(false);

  useEffect(() => {
    openRequestSink();
  }, []);

  return (
    <ModalDialog
      title={isExpired ? 'Session Expired' : 'We could not complete your Sign-in'}
      icon={isExpired ? TimerResetIcon : LockOpenIcon}
      color={isExpired ? 'primary' : 'warning'}
      backdropBlur={true}
    >
      <DialogContent>
        <DialogContentText
          sx={{
            color: 'text.primary',
          }}
        >
          {isExpired
            ? 'For your security, we have signed you out. Please Sign In again to continue.'
            : 'We are unable to grant access at this moment. Please, try to sign in again.'}
        </DialogContentText>
        {!isExpired && ERRORED_SESSION_DESC_MAP.has(reason) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {ERRORED_SESSION_DESC_MAP.get(reason)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          endIcon={<LogInIcon size={18} />}
          onClick={() => {
            if (acceptedRef.current) {
              return;
            }
            acceptedRef.current = true;
            void onAccept();
          }}
        >
          {isExpired ? 'Sign In to Continue' : 'Sign In Again'}
        </Button>
      </DialogActions>
    </ModalDialog>
  );
};

const ERRORED_SESSION_DESC_MAP = new Map<'unknown' | 'invalid' | OAuthAccessDeniedCodeType, `${string}.`>([
  ['unknown', 'Unknown error.'],
  ['invalid', 'Invalid session.'],
  ['email_unverified', 'Your email address is not verified.'],
  ['internal', 'Internal error.'],
  ['invalid_enrollment_state', 'Invalid multi-factor authentication enrollment state.'],
  ['invalid_linking_state', 'Invalid user identity linking state.'],
  ['invalid_linking_token', 'Invalid user identity linking token.'],
  ['invalid_mfa_state', 'Invalid multi-factor authentication state.'],
  ['mfa_required', 'Multi-factor authentication is required.'],
  ['oauth_consent_denied', 'The app was not connected.'],
  ['oauth_consent_invalid', 'Invalid app connection.'],
]);
